import React, { useMemo } from "react";
import { QuickLinksProps } from "./types";
import { Nav } from "components/src/components/Nav/Nav";
import { getTeamInfo } from "shared/src/hooks/getTeamInfo";
import { TeamMatchup } from "../TeamMatchup/TeamMatchup";
import { Avatar } from "components/src/components/Avatar/Avatar";
import { TeamLogo } from "../TeamLogo/TeamLogo";
import type { QuickLinksItemProps } from "./types";
import type { SizeType } from "shared/src/types";
import { Background } from "components/src/components/Background/Background";
import cx from "components/src/utils/cx";
import parseIconString, { IconStringReturnType, isValidIconString } from "components/src/utils/parseIconString";

const teamMatchupSizeMap: Record<
  PropertyKey,
  Record<PropertyKey, `${string}px`>
> = {
  xs: {
    width: "22px",
    height: "22px",
  },
  sm: {
    width: "22px",
    height: "22px",
  },
  md: {
    width: "34px",
    height: "34px",
  },
  lg: {
    width: "34px",
    height: "34px",
  },
};

const avatarSizeMap: Record<PropertyKey, SizeType> = {
  xs: "xl",
  sm: "xl",
  md: "2xl",
  lg: "2xl",
};

const teamMatchupTopMarginList = ["lg", "sm"];

export const QuickLinks = ({ items, size, ...props }: QuickLinksProps) => {
  const _size = size && size in teamMatchupSizeMap ? size : "md";

  const _items = useMemo(() => {
    return items.map((item) => {

      let _team: string | IconStringReturnType | undefined = item?.team;
      let _teams: string[] | IconStringReturnType[] | undefined = item?.teams;
      let parsedLeadingIcon: Record<PropertyKey, any> = {};

      if (item?.leadingIcon) {
        if (typeof item?.leadingIcon === "string") {
          parsedLeadingIcon = parseIconString(item?.leadingIcon) ?? { name: item?.leadingIcon };
        } else if (typeof item?.leadingIcon === "object" && "name" in item?.leadingIcon) {
          parsedLeadingIcon = parseIconString(item?.leadingIcon?.name as string) ?? { name: item?.leadingIcon?.name };
        }
      }
      let _item = { ...item, leadingIcon: typeof item?.leadingIcon === "object" ? { ...item?.leadingIcon, name: parsedLeadingIcon?.name ?? item?.leadingIcon } : { name: parsedLeadingIcon?.name ?? item?.leadingIcon } };    
      
      /**
       * Parsed Icon String
       */
      if (isValidIconString(item?.leadingIcon)) {
        const parsedStr = parseIconString(item.leadingIcon as string);
        if (Array.isArray(parsedStr) && parsedStr.length >= 2) {
          _teams = parsedStr;
        }
        if (typeof parsedStr === "object" && parsedStr && "isTeam" in parsedStr && parsedStr?.isTeam && parsedStr?.foundTeam) _team = parsedStr;
      };

      if (_team) {
        const processedTeam = _team && typeof _team === "object" && _team?.isTeam ? _team?.foundTeam : item?.team ? getTeamInfo(item.team) : null;
        const bgColors = {
          primaryColor: processedTeam ? processedTeam?.metadata.colors?.primary.value : undefined,
          secondaryColor: processedTeam ? processedTeam?.metadata.colors?.secondary.value : undefined,
        };

        const _bgElement = (
          <Background
            colorType={_team && !item?.isLive ? "dark" : "normal"}
            gradient="vertical"
            colorMode="both"
            position="absolute"
            {...bgColors}
            fallbackPrimaryColor="rgba(9, 84, 49, 1)"
            fallbackSecondaryColor="#030303"
          />
        );

        return {
          id: item?.id,
          label: item?.label ? item?.label : processedTeam?.abbreviation,
          leadingIcon: (
            <Avatar
              icon={<TeamLogo name={processedTeam?.name} mode={props?.mode ?? "light"} />}
              size={avatarSizeMap[_size]}
              bgElement={_bgElement}
              borderColor={
                item?.isLive
                  ? "highlight-accent"
                  : processedTeam?.metadata?.colors?.primary?.value ?? "fg-minimal"
              }
              tabIndex={-1}
              mode="dark"
            />
          ),
          onClick: item?.onClick,
          dataQa: item?.dataQa
        };
      }
      
      if (_teams && Array.isArray(_teams) && _teams.length) {
        let firstTeam, secondTeam;
        if (typeof _teams?.at(0) === "object" && typeof _teams?.at(1) === "object") {
          firstTeam = (_teams?.at(0) as IconStringReturnType)?.foundTeam;
          secondTeam = (_teams?.at(1) as IconStringReturnType)?.foundTeam;
        } else if (typeof _teams?.at(0) === "string" && typeof _teams?.at(1) === "string") {
          firstTeam = getTeamInfo(_teams.at(0) as string);
          secondTeam = getTeamInfo(_teams.at(1) as string)
        }

        const teamLabel = firstTeam?.abbreviation && secondTeam?.abbreviation ? `${firstTeam.abbreviation} at ${secondTeam.abbreviation}` : "---";
        return {
          ...item,
          label: item?.label ? item?.label : teamLabel,
          leadingIcon: (
            <TeamMatchup
              firstTeam={firstTeam}
              secondTeam={secondTeam}
              isLive={item?.isLive}
              {...teamMatchupSizeMap[_size]}
              borderColor="fg-minimal"
              className={cx({
                "cui-mt-2xs": teamMatchupTopMarginList.includes(_size),
                "cui-mt-2px": _size === "xs"
              },"")}
              center
            />
          ),
        };
      }
      return _item;
    });
  }, [items, size, props?.mode]);

  return (
    <Nav
      {...props}
      items={_items as QuickLinksItemProps[]}
      variant="stacked"
      size={size}
    />
  );
};
